import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { formatPrice } from '../utils/modelsFormatters'
import { getCheapestTier, getDiscountedPrice } from '../utils/tierPricing'
import { isChatModel, isGenerationModel } from '../types/unified-model.types'
import { ModelUptimeStrip } from './ModelUptimeStrip'
import { PriceFigure } from './ModelCardCells'
import type { ProviderTier } from '../../../../../shared-pricing-config'
import type { UnifiedModel } from '../types/unified-model.types'
import { ProviderTierBadges } from '@/shared/components/ProviderTierBadges'
import { copyModelId } from '@/features/models/utils/modelsFormatters'
import { formatCost, formatPriceUnit } from '@/shared/utils/generationPricing'

interface ModelDetailSidebarProps {
  model: UnifiedModel & { availableTiers?: Array<ProviderTier> }
  enabledTiers: Array<ProviderTier>
  cta: React.ReactNode
}

/** Sticky at-a-glance card: copyable ID, live status, tiers, starting price, CTA. */
export function ModelDetailSidebar({ model, enabledTiers, cta }: ModelDetailSidebarProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await copyModelId(model.id)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <aside className="surface-bevel flex flex-col gap-5 rounded-[1.5rem] p-6 lg:sticky lg:top-28">
      <Row label="Model ID">
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex w-full items-center justify-between gap-2 rounded-xl shadow-none! border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-2 font-mono text-caption text-[var(--color-text-primary)] transition-colors duration-300 ease-brisk hover:border-[var(--color-border-strong)]"
          title="Copy model ID"
        >
          <span className="truncate">{model.id}</span>
          {copied ? (
            <Check className="h-3.5 w-3.5 shrink-0 text-[var(--color-success-text)]" />
          ) : (
            <Copy className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-tertiary)]" />
          )}
        </button>
      </Row>

      <ModelUptimeStrip model={model} />

      {model.availableTiers && model.availableTiers.length > 0 && (
        <Row label="Tiers">
          <ProviderTierBadges
            availableTiers={model.availableTiers}
            enabledTiers={enabledTiers}
            size="sm"
          />
        </Row>
      )}

      <div className="border-t border-[var(--color-border)] pt-5">
        <PriceSummary model={model} />
      </div>

      <div className="[&>*]:w-full">{cta}</div>
    </aside>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="eyebrow text-[0.6875rem]">
        {label}
      </span>
      {children}
    </div>
  )
}

function PriceSummary({ model }: { model: ModelDetailSidebarProps['model'] }) {
  if (isChatModel(model)) {
    const p = model.pricing
    const hasDiscount = !!model.hasDiscount && (model.discountPercent ?? 0) > 0
    const { tier, multiplier } = getCheapestTier(model.id, model.availableTiers)
    const figure = (price: number) =>
      formatPrice(getDiscountedPrice(price, hasDiscount, model.discountPercent) * multiplier)

    return (
      <>
        <div className="flex items-center justify-between font-mono text-[0.6875rem] uppercase tracking-[0.1em] text-[var(--color-text-tertiary)]">
          <span>
            From <span className="font-semibold capitalize text-[var(--color-text-secondary)]">{tier}</span>
          </span>
          <span>$ / 1M tokens</span>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-4">
          <PriceFigure label="Input" value={figure(p.inputPricePerMillion)} unit="/M" />
          <PriceFigure label="Output" value={figure(p.outputPricePerMillion)} unit="/M" />
        </dl>
      </>
    )
  }

  if (isGenerationModel(model)) {
    return (
      <dl>
        <PriceFigure
          label={`Price ${formatPriceUnit(model.pricing.priceUnit)}`}
          value={formatCost(model.pricing.priceMillidollars)}
        />
      </dl>
    )
  }

  return null
}
